import { useContactInvalidation } from "@/hooks/use-contact-invalidation";
import {
  createContactFaxV1,
  deleteContactFaxV1,
  updateContactFaxV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Contact } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy } from "lodash";
import toast from "react-hot-toast";
import { type ContactSubitemMutationOptions } from "./contact-subitem-mutation-options";

export const useCreateContactFax = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof createContactFaxV1>[0]) =>
      createContactFaxV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldFaxes = oldData.data.faxes;
          if (data.isPrimary) {
            oldFaxes.forEach((fax) => {
              if (fax.id !== data.id) {
                fax.isPrimary = false;
              }
            });
          }
          return {
            data: {
              ...oldData.data,
              faxes: orderBy([...oldFaxes, data], ["isPrimary"], ["desc"]),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating contact fax");
    },

    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact fax created successfully");
      }
    },
  });
};

export const useUpdateContactFax = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();

  return useMutation({
    mutationFn: (data: Parameters<typeof updateContactFaxV1>[0]) =>
      updateContactFaxV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldFaxes = oldData.data.faxes;
          if (data.isPrimary) {
            oldFaxes.forEach((fax) => {
              if (fax.id !== data.id) {
                fax.isPrimary = false;
              }
            });
          }
          return {
            data: {
              ...oldData.data,
              faxes: orderBy(
                oldFaxes.map((fax) => {
                  return fax.id === data.id ? data : fax;
                }),
                ["isPrimary"],
                ["desc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating contact fax");
    },

    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact fax updated successfully");
      }
    },
  });
};

export const useDeleteContactFax = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();

  return useMutation({
    mutationFn: (data: Parameters<typeof deleteContactFaxV1>[0]) =>
      deleteContactFaxV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              faxes: oldData.data.faxes.filter((fax) => {
                return fax.id !== data.id;
              }),
              ...data,
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toast.error("Error deleting contact fax");
    },
    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact fax deleted successfully");
      }
    },
  });
};
